import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { API_BASE_URL } from "../../../utils/api";
import { getPermissions } from "../../../utils/permissions";
import { DeleteIcon, CheckIcon, AlertIcon } from "../icons/Icons";
import TimetableCampusPage from "../../superadmin/teachers/TimetableCampusPage";
import ViewTimetablePage from "../../superadmin/teachers/ViewTimetablePage";

/* ---- Layout shell ---- */
import Sidebar from "./Sidebar.js";
import Navbar from "./Navbar.js";
import RequireRole from "../auth/RequireRole.js";

/* ---- Page components ---- */
import DashboardHome from "../dashboard/DashboardHome";
import StudentForm from "../students/StudentForm";
import RecordManagementPage from "../students/RecordManagementPage";
import StudentListBase from "../students/StudentListBase";
import FeeManagement from "../fees/FeeManagement";
import { fetchFeeAssignments } from "../fees/feeApi";
import CsvExportPage from "../../superadmin/CsvExportPage";
import CampusDashboard from "../../superadmin/CampusDashboard";
import SectionsManagement from "../../superadmin/CampusDashboard/SectionsManagement";
import AdminManagement from "../../superadmin/AdminManagement";
import SystemReports from "../../superadmin/SystemReports";
import ViewerDashboardPage from "../../viewer/ViewerDashboardPage";
import ViewerCampusAnalyticsPage from "../../viewer/ViewerCampusAnalyticsPage";
import ControllerSchoolReportsPage from "../../controller/ControllerSchoolReportsPage";
import ControllerStudentReportsPage from "../../controller/ControllerStudentReportsPage";
import ControllerPassedOutReportsPage from "../../controller/ControllerPassedOutReportsPage";
import ControllerSystemReportsPage from "../../controller/ControllerSystemReportsPage";
import ControllerCampusAnalyticsPage from "../../controller/ControllerCampusAnalyticsPage";
import CampusFeePage from "../fees/CampusFeePage";
import FeeCampusSelectPage from "../fees/FeeCampusSelectPage";

/* ---- Admin Report Components ---- */
import SchoolReports from "../reports/SchoolReports";
import StudentReports from "../reports/StudentReports";
import PassedOutReports from "../reports/PassedOutReports";

/* ---- Fee components ---- */
import StudentFeeDetailsPage from "../fees/StudentFeeDetailsPage";
import BulkChallanGenerationPage from "../fees/BulkChallanGenerationPage";

/* ---- Teacher components ---- */
import TeacherForm from "../../superadmin/teachers/TeacherForm";
import CourseCampusSelectPage from "../../superadmin/teachers/CourseCampusSelectPage";
import CourseCampusHub from "../../superadmin/teachers/CourseCampusHub";
import AddCoursePage from "../../superadmin/teachers/AddCoursePage";
import CampusCourseAssignPage from "../../superadmin/teachers/CampusCourseAssignPage";
import TeacherReportsPage from "../../superadmin/teachers/TeacherReportsPage";

import TeacherDashboard from "../../teacher/TeacherDashboard";
import ParentDashboard from "../../parent/ParentDashboard";
import YearlyCalendarPage from "../calendar/YearlyCalendarPage";
import ProcurementPage from "../procurement/ProcurementPage";
import DailyReportPage from "../dailyreport/DailyReportPage";
import DailyReportSuperadminPage from "../dailyreport/DailyReportSuperadminPage";

const TEACHERS_STORAGE_KEY = "als_teachers";

function loadLocalList(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (err) {
    console.error(`Failed to parse localStorage key "${key}"`, err);
    return fallback;
  }
}

function saveLocalList(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(`Failed to write localStorage key "${key}"`, err);
  }
}

function DashboardLayout({ onLogout }) {
  const loginRole = (localStorage.getItem("als_role") || "").toLowerCase();
  const permissions = getPermissions(loginRole);
  const canManageCampuses =
    permissions.canManageCampuses ||
    loginRole === "superadmin" ||
    loginRole === "principal" ||
    loginRole === "administrator" ||
    loginRole === "admin";
  // Teachers can be registered by the same roles that manage campuses today.
  // If you add a dedicated `canRegisterTeachers` flag to utils/permissions.js,
  // swap the line below for `permissions.canRegisterTeachers`.
  const canRegisterTeachers =
    permissions.canRegisterTeachers ??
    (loginRole === "superadmin" ||
      loginRole === "principal" ||
      loginRole === "administrator" ||
      loginRole === "admin");
  // Course assignment uses the same access as teacher registration unless
  // you add a dedicated `canAssignCourses` flag to utils/permissions.js.
  const canAssignCourses = permissions.canAssignCourses ?? canRegisterTeachers;
  const canViewTeachers = permissions.canViewTeachers ?? canRegisterTeachers;
  const canViewTeacherAssignments =
    permissions.canViewTeacherAssignments ?? canAssignCourses;
  const canViewTeacherTimetable =
    permissions.canViewTeacherTimetable ?? canAssignCourses;
  const canViewAcademicAdminReports =
    permissions.canViewAcademicReports &&
    loginRole !== "admin" &&
    loginRole !== "teacher" &&
    loginRole !== "accountant";
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [feeAssignments, setFeeAssignments] = useState({});
  const [stats, setStats] = useState({
    total_students: 0,
    active_students: 0,
    new_this_month: 0,
    withdrawn_count: 0,
  });
  const [toast, setToast] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [isDeletingStudent, setIsDeletingStudent] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [teacherCampuses, setTeacherCampuses] = useState([]);

  // Keep teachers/parents inside their portal — /dashboard is for admin roles and
  // used to trigger session-clearing campus fetches.
  useEffect(() => {
    if (permissions.isTeacher && location.pathname === "/dashboard") {
      navigate("/teacher/overview", { replace: true });
    }
    if (permissions.isParent && location.pathname === "/dashboard") {
      navigate("/parent/overview", { replace: true });
    }
  }, [permissions.isTeacher, permissions.isParent, location.pathname, navigate]);

  // Teacher registration's Campus dropdown should show your real campuses
  // (the same ones managed under Campus Management), not a local placeholder list.
  // Only admins who can register teachers need this — never run for teachers.
  useEffect(() => {
    if (!canRegisterTeachers) return;

    const fetchTeacherCampuses = async () => {
      const token = localStorage.getItem("als_token");
      try {
        const res = await fetch(`${API_BASE_URL}/api/campuses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error("Failed to fetch campuses");
        const data = await res.json();
        setTeacherCampuses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch campuses for teacher form", err);
      }
    };
    fetchTeacherCampuses();
  }, [onLogout, canRegisterTeachers]);

  const fetchStudents = React.useCallback(async () => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(`${API_BASE_URL}/api/students`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) throw new Error("Failed to fetch students");
      const data = await res.json();
      // Keep both numeric id (for API) and student_id string (for display)
      setStudents(
        Array.isArray(data)
          ? data.map((s) => ({ ...s, id: s.id, student_id: s.student_id }))
          : [],
      );
    } catch (err) {
      console.error("Failed to fetch students", err);
    }
  }, [onLogout]);

  const fetchStats = React.useCallback(async () => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(`${API_BASE_URL}/api/students/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) throw new Error("Failed to fetch stats");
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats", err);
    }
  }, [onLogout]);

  const fetchFeeData = React.useCallback(async () => {
    try {
      const assignments = await fetchFeeAssignments();
      setFeeAssignments(assignments || {});
    } catch (err) {
      console.error("Failed to fetch fee assignments", err);
    }
  }, []);

  // Load once on mount so deep-linked routes (e.g. /fees) have data too.
  useEffect(() => {
    if (!permissions.canLoadStudentData) return;
    fetchStudents();
    fetchStats();
    if (permissions.canViewFees) fetchFeeData();
  }, [
    permissions.canLoadStudentData,
    permissions.canViewFees,
    fetchStudents,
    fetchStats,
    fetchFeeData,
  ]);

  // Refresh whenever the user navigates back to the dashboard.
  useEffect(() => {
    if (permissions.canLoadStudentData && location.pathname === "/dashboard") {
      fetchStudents();
      fetchStats();
      if (permissions.canViewFees) fetchFeeData();
    }
  }, [
    location.pathname,
    permissions.canLoadStudentData,
    permissions.canViewFees,
    fetchStudents,
    fetchStats,
    fetchFeeData,
  ]);

  const showToast = (type, title, message) => {
    setToast({ type, title, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleRegisterStudent = async (student) => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(`${API_BASE_URL}/api/students`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: student.name,
          father_name: student.father_name || null,
          father_phone: student.father_phone || null,
          father_cnic: student.father_cnic || null,
          father_address: student.father_address || null,
          cnic_bform: student.cnic_bform || null,
          current_address: student.current_address || null,
          permanent_address: student.permanent_address || null,
          is_transfer_student: Boolean(student.is_transfer_student),
          previous_school_name: student.previous_school_name || null,
          previous_class: student.previous_class || null,
          previous_grade: student.previous_grade || null,
          previous_academic_record: student.previous_academic_record || null,
          gender: student.gender,
          date_of_birth: student.date_of_birth || null,
          campus_id: student.campus_id ? parseInt(student.campus_id, 10) : null,
          class_id: student.class_id ? parseInt(student.class_id, 10) : null,
          class_name: student.class_id ? undefined : student.class_name || null,
          class_section: student.class_section || null,
          academic_session: student.academic_session || null,
          admission_date: student.admission_date,
          student_status: student.student_status || "Active",
          actual_fee_amount: student.fee_amount
            ? parseFloat(student.fee_amount)
            : null,
          fee_plan_name: student.fee_plan_name || null,
          scholarship_id: student.scholarship_option
            ? null
            : student.scholarship_id
              ? parseInt(student.scholarship_id, 10)
              : null,
          scholarship_option: student.scholarship_option || null,
          custom_scholarship_amount: student.custom_scholarship_amount
            ? parseFloat(student.custom_scholarship_amount)
            : null,
          fee_status: student.fee_status,
          guardian_name: student.guardian_name || null,
          guardian_relation: student.guardian_relation || null,
          guardian_phone: student.guardian_phone || null,
          guardian_email: student.guardian_email || null,
          guardian_address: student.guardian_address || null,
          guardian_cnic: student.guardian_cnic || null,
          emergency_contact_name: student.emergency_contact_name || null,
          emergency_contact_number: student.emergency_contact_number || null,
          photo_data: student.photo_data || null,
        }),
      });

      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail || JSON.stringify(errData);
        console.error("Registration error response:", errData);
        throw new Error(detail);
      }

      const created = await res.json().catch(() => ({}));
      showToast(
        "success",
        "Student Registered",
        `${student.name} has been successfully registered.`,
      );
      fetchStudents();
      fetchStats();
      return created && typeof created === "object" ? created : true;
    } catch (err) {
      console.error(err);
      showToast(
        "error",
        "Registration Failed",
        err.message || "Could not register student in the database.",
      );
      return false;
    }
  };

  const handleUpdateStudent = async (student) => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/students/${student.student_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: student.name,
            father_name: student.father_name || null,
            cnic_bform: student.cnic_bform || null,
            current_address: student.current_address || null,
            permanent_address: student.permanent_address || null,
            is_transfer_student: Boolean(student.is_transfer_student),
            previous_school_name: student.previous_school_name || null,
            previous_class: student.previous_class || null,
            previous_grade: student.previous_grade || null,
            previous_academic_record: student.previous_academic_record || null,
            gender: student.gender,
            date_of_birth: student.date_of_birth || null,
            campus_id: student.campus_id
              ? parseInt(student.campus_id, 10)
              : null,
            class_id: student.class_id ? parseInt(student.class_id, 10) : null,
            class_name: student.class_id
              ? undefined
              : student.class_name || null,
            class_section: student.class_section || null,
            academic_session: student.academic_session || null,
            admission_date: student.admission_date,
            student_status: student.student_status || "Active",
            actual_fee_amount: student.fee_amount
              ? parseFloat(student.fee_amount)
              : null,
            fee_plan_name: student.fee_plan_name || null,
            scholarship_id: student.scholarship_option
              ? null
              : student.scholarship_id
                ? parseInt(student.scholarship_id, 10)
                : null,
            scholarship_option: student.scholarship_option || null,
            custom_scholarship_amount: student.custom_scholarship_amount
              ? parseFloat(student.custom_scholarship_amount)
              : null,
            fee_status: student.fee_status || "Unpaid",
            guardian_name: student.guardian_name || null,
            guardian_relation: student.guardian_relation || null,
            guardian_phone: student.guardian_phone || null,
            guardian_email: student.guardian_email || null,
            guardian_address: student.guardian_address || null,
            guardian_cnic: student.guardian_cnic || null,
            emergency_contact_name: student.emergency_contact_name || null,
            emergency_contact_number: student.emergency_contact_number || null,
            photo_data: student.photo_data || null,
          }),
        },
      );

      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to update student");
      }

      showToast(
        "success",
        "Student Updated",
        `${student.name} record has been updated.`,
      );
      setEditStudent(null);
      fetchStudents();
      fetchStats();
      navigate("/dashboard");
      return true;
    } catch (err) {
      console.error(err);
      showToast("error", "Update Failed", err.message);
      return false;
    }
  };

  // Used by the inline Edit panel in manage mode — same API call as
  // handleUpdateStudent but WITHOUT navigating away from the page.
  const handleInlineSaveEdit = async (student) => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/students/${student.student_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: student.name,
            father_name: student.father_name || null,
            cnic_bform: student.cnic_bform || null,
            current_address: student.current_address || null,
            permanent_address: student.permanent_address || null,
            is_transfer_student: Boolean(student.is_transfer_student),
            previous_school_name: student.previous_school_name || null,
            previous_class: student.previous_class || null,
            previous_grade: student.previous_grade || null,
            previous_academic_record: student.previous_academic_record || null,
            gender: student.gender,
            date_of_birth: student.date_of_birth || null,
            campus_id: student.campus_id
              ? parseInt(student.campus_id, 10)
              : null,
            class_id: student.class_id ? parseInt(student.class_id, 10) : null,
            class_name: student.class_id ? undefined : student.class_name || null,
            class_section: student.class_section || null,
            admission_date: student.admission_date,
            student_status: student.student_status || "Active",
            actual_fee_amount: student.fee_amount
              ? parseFloat(student.fee_amount)
              : null,
            fee_plan_name: student.fee_plan_name || null,
            scholarship_id: student.scholarship_option
              ? null
              : student.scholarship_id
                ? parseInt(student.scholarship_id, 10)
                : null,
            scholarship_option: student.scholarship_option || null,
            custom_scholarship_amount: student.custom_scholarship_amount
              ? parseFloat(student.custom_scholarship_amount)
              : null,
            fee_status: student.fee_status || "Unpaid",
            guardian_name: student.guardian_name || null,
            guardian_relation: student.guardian_relation || null,
            guardian_phone: student.guardian_phone || null,
            guardian_email: student.guardian_email || null,
            guardian_address: student.guardian_address || null,
            guardian_cnic: student.guardian_cnic || null,
            emergency_contact_name: student.emergency_contact_name || null,
            emergency_contact_number: student.emergency_contact_number || null,
            photo_data: student.photo_data || null,
          }),
        },
      );

      if (res.status === 401) {
        onLogout();
        return false;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to update student");
      }

      showToast(
        "success",
        "Student Updated",
        `${student.name} record has been updated.`,
      );
      // Update the local students list so the table reflects changes immediately
      const updated = await res
        .clone()
        .json()
        .catch(() => student);
      setStudents((prev) =>
        prev.map((s) =>
          s.id === (updated.id ?? student.id) ? { ...s, ...updated } : s,
        ),
      );
      fetchStats();
      return true;
    } catch (err) {
      console.error(err);
      showToast("error", "Update Failed", err.message);
      return false;
    }
  };

  const handleDeleteStudent = async (student, password) => {
    const token = localStorage.getItem("als_token");
    const apiStudentId =
      student?.student_id || student?.studentId || student?.id;
    try {
      setIsDeletingStudent(true);
      const res = await fetch(`${API_BASE_URL}/api/students/${apiStudentId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });

      if (res.status === 401) {
        const errData = await res.json().catch(() => ({}));
        if (errData.detail === "Invalid password") {
          showToast(
            "error",
            "Authentication Failed",
            "The password you entered is incorrect.",
          );
          return;
        }
        onLogout();
        return;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to delete student");
      }

      setStudents((prev) => prev.filter((s) => s.id !== student.id));
      setDeleteModal(null);
      setDeletePassword("");
      showToast(
        "success",
        "Student Removed",
        "The student record has been deleted from the database.",
      );
      fetchStats();
    } catch (err) {
      console.error(err);
      showToast(
        "error",
        "Deletion Failed",
        "Could not delete student from the database.",
      );
    } finally {
      setIsDeletingStudent(false);
    }
  };

  const handleDeleteFilteredStudents = async (studentRecords, password) => {
    const token = localStorage.getItem("als_token");
    const studentIds = studentRecords
      .map((student) => student?.student_id || student?.studentId)
      .filter(Boolean);

    if (studentIds.length === 0) {
      showToast(
        "error",
        "Bulk Deletion Failed",
        "No valid student IDs were selected.",
      );
      throw new Error("No valid student IDs were selected.");
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/students/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          student_ids: studentIds,
          password,
        }),
      });

      if (res.status === 401) {
        const errData = await res.json().catch(() => ({}));
        if (errData.detail === "Invalid password") {
          showToast(
            "error",
            "Authentication Failed",
            "The password you entered is incorrect.",
          );
          throw new Error("Invalid password");
        }
        onLogout();
        throw new Error("Session expired");
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(
          errData.detail || "Could not delete selected students.",
        );
      }

      const result = await res.json();
      const missingIds = result.missing_ids || [];
      const deletedStudentIds = new Set(
        studentIds.filter((id) => !missingIds.includes(id)),
      );
      const deletedStudents = studentRecords.filter((s) =>
        deletedStudentIds.has(s.student_id || s.studentId),
      );
      setStudents((prev) =>
        prev.filter((s) => !deletedStudentIds.has(s.student_id || s.studentId)),
      );
      fetchStats();

      if (missingIds.length > 0) {
        showToast(
          "error",
          "Bulk Deletion Partial",
          `${result.deleted_count} deleted, ${missingIds.length} not found.`,
        );
      } else {
        showToast(
          "success",
          "Bulk Deletion Complete",
          `${result.deleted_count} student records deleted.`,
        );
      }
      return deletedStudents;
    } catch (err) {
      console.error(err);
      if (
        err.message !== "Invalid password" &&
        err.message !== "Session expired"
      ) {
        showToast(
          "error",
          "Bulk Deletion Failed",
          err.message || "Could not delete selected students.",
        );
      }
      throw err;
    }
  };

  const handleWithdrawStudent = async (student, withdrawalData) => {
    const token = localStorage.getItem("als_token");
    const apiStudentId =
      student?.student_id || student?.studentId || student?.id;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/students/${apiStudentId}/withdraw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            withdrawal_date: withdrawalData.withdrawal_date,
            reason: withdrawalData.reason,
          }),
        },
      );

      if (res.status === 401) {
        onLogout();
        return null;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to withdraw student");

      const updatedStudent = {
        ...data,
        id: data.id,
        student_id: data.student_id,
      };
      setStudents((prev) =>
        prev.map((s) => (s.id === updatedStudent.id ? updatedStudent : s)),
      );
      fetchStats();
      showToast(
        "success",
        "Student Withdrawn",
        `${updatedStudent.name} has been marked as withdrawn.`,
      );
      return updatedStudent;
    } catch (err) {
      console.error(err);
      showToast(
        "error",
        "Withdrawal Failed",
        err.message || "Could not withdraw student.",
      );
      return null;
    }
  };

  // ── Promotion ───────────────────────────────────────────────────────────
  const promoteOneStudent = async (student, payload) => {
    const token = localStorage.getItem("als_token");
    const apiStudentId = student?.student_id || student?.id;
    const res = await fetch(`${API_BASE_URL}/api/students/${apiStudentId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (res.status === 401) {
      onLogout();
      throw new Error("__unauthorized__");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Failed to promote student");
    return { ...data, id: data.id, student_id: data.student_id };
  };

  const handlePromoteStudent = async (student, payload) => {
    try {
      const updated = await promoteOneStudent(student, payload);
      setStudents((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s)),
      );
      fetchStats();
      showToast(
        "success",
        "Student Promoted",
        `${updated.name} promoted to ${updated.class_name || updated.student_status}.`,
      );
      return updated;
    } catch (err) {
      if (err.message !== "__unauthorized__") {
        console.error(err);
        showToast(
          "error",
          "Promotion Failed",
          err.message || `Could not promote ${student.name}.`,
        );
      }
      return null;
    }
  };

  const handlePromoteFiltered = async (studentRecords, payload) => {
    const results = [];
    let failedCount = 0;
    for (const student of studentRecords) {
      try {
        const updated = await promoteOneStudent(student, payload);
        results.push({ before: student, after: updated });
      } catch (err) {
        if (err.message === "__unauthorized__") return results;
        failedCount += 1;
      }
    }
    if (results.length > 0) {
      setStudents((prev) =>
        prev.map((s) => {
          const match = results.find((r) => r.after.id === s.id);
          return match ? match.after : s;
        }),
      );
      fetchStats();
    }
    if (failedCount > 0) {
      showToast(
        "error",
        "Promotion Partial",
        `${results.length} promoted, ${failedCount} failed.`,
      );
    } else {
      showToast(
        "success",
        "Class Promoted",
        `${results.length} student record(s) promoted.`,
      );
    }
    return results;
  };

  const goToRegister = (student) => {
    setEditStudent(student);
    navigate("/students/register");
  };

  // ── Teacher registration (local only — no backend) ────────────────────
  const handleRegisterTeacher = async (teacher) => {
    try {
      const existing = loadLocalList(TEACHERS_STORAGE_KEY, []);

      const duplicate = existing.some(
        (t) =>
          t.cnic &&
          teacher.cnic &&
          String(t.cnic).trim() === String(teacher.cnic).trim(),
      );
      if (duplicate) {
        showToast(
          "error",
          "Already Registered",
          "A teacher with this CNIC is already registered.",
        );
        return false;
      }

      const newTeacher = {
        ...teacher,
        id: Date.now(),
        created_at: new Date().toISOString(),
      };
      const updated = [...existing, newTeacher];
      saveLocalList(TEACHERS_STORAGE_KEY, updated);

      showToast(
        "success",
        "Teacher Registered",
        `${teacher.name} has been successfully registered.`,
      );
      navigate("/dashboard");
      return true;
    } catch (err) {
      console.error("Failed to save teacher locally", err);
      showToast(
        "error",
        "Registration Failed",
        "Could not save the teacher record.",
      );
      return false;
    }
  };

  return (
    <div className="dashboard">
      <Sidebar
        loginRole={loginRole}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={onLogout}
      />

      <div
        className={`main-content ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Navbar
          isEditingStudent={Boolean(editStudent)}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          loginRole={loginRole}
          onLogout={onLogout}
        />

        <Routes>
          <Route
            path="dashboard"
            element={
              permissions.isViewer ? (
                <ViewerDashboardPage />
              ) : (
                <DashboardHome
                  students={students}
                  apiStats={stats}
                  permissions={permissions}
                  feeAssignments={feeAssignments}
                />
              )
            }
          />
          <Route
            path="calendar"
            element={
              <RequireRole allowed={permissions.canViewCalendar}>
                <YearlyCalendarPage />
              </RequireRole>
            }
          />
          <Route
            path="viewer/campus-analytics"
            element={
              <RequireRole allowed={permissions.isViewer}>
                <ViewerCampusAnalyticsPage
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="students/register"
            element={
              <RequireRole allowed={permissions.canRegisterStudents}>
                <StudentForm
                  onSave={
                    editStudent ? handleUpdateStudent : handleRegisterStudent
                  }
                  onCancel={() => {
                    navigate("/dashboard");
                    setEditStudent(null);
                  }}
                  userRole={loginRole}
                  initialData={editStudent}
                  showToast={showToast}
                  onLogout={onLogout}
                />
              </RequireRole>
            }
          />
          <Route
            path="teachers/register"
            element={
              <RequireRole allowed={canRegisterTeachers}>
                <TeacherForm
                  onSave={handleRegisterTeacher}
                  onCancel={() => navigate("/dashboard")}
                  showToast={showToast}
                  campuses={teacherCampuses}
                />
              </RequireRole>
            }
          />
          <Route
            path="teachers/courses"
            element={
              <RequireRole allowed={canViewTeacherAssignments}>
                <CourseCampusSelectPage
                  onLogout={onLogout}
                  showToast={showToast}
                />
              </RequireRole>
            }
          />
          <Route
            path="teachers/courses/campus/:campusId"
            element={
              <RequireRole allowed={canViewTeacherAssignments}>
                <CourseCampusHub readOnly={!canAssignCourses} />
              </RequireRole>
            }
          />
          <Route
            path="teachers/courses/campus/:campusId/add"
            element={
              <RequireRole allowed={canAssignCourses}>
                <AddCoursePage onLogout={onLogout} showToast={showToast} />
              </RequireRole>
            }
          />
          <Route
            path="teachers/courses/campus/:campusId/assign"
            element={
              <RequireRole allowed={canViewTeacherAssignments}>
                <CampusCourseAssignPage
                  onLogout={onLogout}
                  showToast={showToast}
                  readOnly={!canAssignCourses}
                />
              </RequireRole>
            }
          />
          <Route
            path="teachers/reports"
            element={
              <RequireRole allowed={canViewTeachers}>
                <TeacherReportsPage />
              </RequireRole>
            }
          />
          <Route
            path="teachers/timetable"
            element={
              <RequireRole allowed={canAssignCourses}>
                <CourseCampusSelectPage
                  onLogout={onLogout}
                  showToast={showToast}
                  basePath="/teachers/timetable/campus"
                  title="Select Campus — Timetable"
                />
              </RequireRole>
            }
          />
          <Route
            path="teachers/timetable/campus/:campusId"
            element={
              <RequireRole allowed={canAssignCourses}>
                <TimetableCampusPage
                  onLogout={onLogout}
                  showToast={showToast}
                />
              </RequireRole>
            }
          />
          <Route
            path="teachers/view-timetable"
            element={
              <RequireRole allowed={canViewTeacherTimetable}>
                <ViewTimetablePage onLogout={onLogout} showToast={showToast} />
              </RequireRole>
            }
          />
          <Route
            path="students/manage"
            element={
              <RequireRole allowed={permissions.canViewStudents}>
                <RecordManagementPage
                  students={students}
                  onDeleteClick={(student) => setDeleteModal(student)}
                  onEditClick={goToRegister}
                  onSaveEdit={handleInlineSaveEdit}
                  onDeleteFiltered={handleDeleteFilteredStudents}
                  onWithdrawStudent={handleWithdrawStudent}
                  onPromoteStudent={handlePromoteStudent}
                  onPromoteFiltered={handlePromoteFiltered}
                  userRole={loginRole}
                  onLogout={onLogout}
                  showToast={showToast}
                  canEdit={permissions.canUpdateStudents}
                  canDelete={permissions.canDeleteStudents}
                  canWithdraw={permissions.canWithdrawStudents}
                />
              </RequireRole>
            }
          />
          <Route
            path="students/bulk-delete"
            element={
              <RequireRole allowed={permissions.canDeleteStudents}>
                <StudentListBase
                  students={students}
                  onDeleteClick={(student) => setDeleteModal(student)}
                  onDeleteFiltered={handleDeleteFilteredStudents}
                  userRole={loginRole}
                  onLogout={onLogout}
                  showToast={showToast}
                  mode="delete"
                  canEdit={false}
                  canDelete={permissions.canDeleteStudents}
                  canWithdraw={false}
                />
              </RequireRole>
            }
          />
          {/* Old View/Update/Delete pages are merged into Record Management above. */}
          <Route
            path="students/records"
            element={<Navigate to="/students/manage" replace />}
          />
          <Route
            path="students/update"
            element={<Navigate to="/students/manage" replace />}
          />
          <Route
            path="students/delete"
            element={<Navigate to="/students/bulk-delete" replace />}
          />
          <Route
            path="principal/school-reports"
            element={
              <RequireRole allowed={canViewAcademicAdminReports}>
                <SchoolReports students={students} />
              </RequireRole>
            }
          />
          <Route
            path="principal/student-reports"
            element={
              <RequireRole allowed={canViewAcademicAdminReports}>
                <StudentReports students={students} />
              </RequireRole>
            }
          />
          <Route
            path="principal/passed-out"
            element={
              <RequireRole allowed={canViewAcademicAdminReports}>
                <PassedOutReports students={students} />
              </RequireRole>
            }
          />
          <Route
            path="admin/school-reports"
            element={
              <RequireRole allowed={permissions.isAdmin}>
                <ControllerSchoolReportsPage students={students} />
              </RequireRole>
            }
          />
          <Route
            path="admin/student-reports"
            element={
              <RequireRole allowed={permissions.isAdmin}>
                <ControllerStudentReportsPage students={students} />
              </RequireRole>
            }
          />
          <Route
            path="admin/passed-out"
            element={
              <RequireRole allowed={permissions.isAdmin}>
                <ControllerPassedOutReportsPage students={students} />
              </RequireRole>
            }
          />
          <Route
            path="admin/system-reports"
            element={
              <RequireRole allowed={permissions.isAdmin}>
                <ControllerSystemReportsPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/campus-analytics"
            element={
              <RequireRole allowed={permissions.isAdmin}>
                <ControllerCampusAnalyticsPage
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="fees"
            element={
              <RequireRole allowed={permissions.canViewFees}>
                {permissions.isSuperadmin ? (
                  <FeeCampusSelectPage
                    onLogout={onLogout}
                    showToast={showToast}
                  />
                ) : (
                  <FeeManagement students={students} />
                )}
              </RequireRole>
            }
          />
          <Route
            path="student-fee-details/:studentId"
            element={
              <RequireRole allowed={permissions.canViewFees}>
                <StudentFeeDetailsPage />
              </RequireRole>
            }
          />
          <Route
            path="bulk-challan-generation"
            element={
              <RequireRole allowed={permissions.canViewFees}>
                <BulkChallanGenerationPage />
              </RequireRole>
            }
          />
          <Route
            path="fees/campus/:campusId"
            element={
              <RequireRole allowed={permissions.canViewFees}>
                <CampusFeePage onLogout={onLogout} showToast={showToast} />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/csv-export"
            element={
              <RequireRole allowed={permissions.isSuperadmin}>
                <CsvExportPage onLogout={onLogout} showToast={showToast} />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses/:campusId/sections"
            element={
              <RequireRole allowed={canManageCampuses}>
                <SectionsManagement onLogout={onLogout} showToast={showToast} />
              </RequireRole>
            }
          />
          {/* STAFF ROUTE - MUST BE BEFORE THE GENERIC :campusId ROUTE */}
          <Route
            path="superadmin/campuses/:campusId/staff"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses/:campusId"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses/:campusId/sessions"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses/:campusId/classes"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses/:campusId/session-assignments"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses/:campusId/section-assignments"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/campuses/:campusId/classes/:classId"
            element={
              <RequireRole allowed={canManageCampuses}>
                <CampusDashboard
                  onLogout={onLogout}
                  showToast={showToast}
                  students={students}
                />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/admins"
            element={
              <RequireRole allowed={permissions.isSuperadmin}>
                <AdminManagement onLogout={onLogout} showToast={showToast} />
              </RequireRole>
            }
          />
          <Route
            path="superadmin/reports"
            element={
              <RequireRole allowed={permissions.isSuperadmin}>
                <SystemReports students={students} />
              </RequireRole>
            }
          />

          <Route
            path="teacher/*"
            element={
              <RequireRole allowed={permissions.isTeacher}>
                <TeacherDashboard onLogout={onLogout} />
              </RequireRole>
            }
          />
          <Route
            path="parent/*"
            element={
              <RequireRole allowed={permissions.isParent}>
                <ParentDashboard onLogout={onLogout} />
              </RequireRole>
            }
          />
          {/* ── Procurement Filing ── accessible by teacher, admin, principal, superadmin ── */}
          <Route
            path="procurement"
            element={
              <RequireRole allowed={
                ["teacher", "admin", "administrator", "principal", "superadmin"]
                  .includes(loginRole)
              }>
                <ProcurementPage showToast={showToast} />
              </RequireRole>
            }
          />

          {/* ── Daily Report — filing (admin / principal) ── */}
          <Route
            path="daily-report"
            element={
              <RequireRole allowed={
                ["admin", "administrator", "principal"].includes(loginRole)
              }>
                <DailyReportPage showToast={showToast} />
              </RequireRole>
            }
          />

          {/* ── Daily Report — overview (superadmin sees all campuses) ── */}
          <Route
            path="daily-report/overview"
            element={
              <RequireRole allowed={loginRole === "superadmin"}>
                <DailyReportSuperadminPage showToast={showToast} />
              </RequireRole>
            }
          />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="modal-overlay">
          <div className="delete-student-modal">
            <div className="delete-student-modal-header">
              <div className="delete-student-modal-icon">
                <DeleteIcon />
              </div>
              <div>
                <h3>Delete Student?</h3>
                <p>This action requires password authentication.</p>
              </div>
            </div>

            <div className="delete-student-modal-body">
              <div className="delete-student-warning">
                <strong>{deleteModal.name}</strong>
                <span>{deleteModal.student_id || deleteModal.id}</span>
                <p>
                  This student record will be permanently deleted and cannot be
                  restored.
                </p>
              </div>

              <label
                className="delete-student-password-label"
                htmlFor="delete-student-password"
              >
                Password
              </label>
              <input
                id="delete-student-password"
                className="delete-student-password-input"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your account password"
                autoComplete="current-password"
                disabled={isDeletingStudent}
                autoFocus
              />
            </div>

            <div className="delete-student-modal-actions">
              <button
                className="delete-student-cancel"
                onClick={() => {
                  setDeleteModal(null);
                  setDeletePassword("");
                }}
                disabled={isDeletingStudent}
              >
                Cancel
              </button>
              <button
                className="delete-student-confirm"
                onClick={() => handleDeleteStudent(deleteModal, deletePassword)}
                disabled={isDeletingStudent || !deletePassword}
              >
                {isDeletingStudent ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeletingStudent && (
        <div className="delete-progress-overlay">
          <div className="delete-progress-card">
            <div className="delete-progress-spinner" />
            <h3>Deleting student record</h3>
            <p>Please wait while the database is updated.</p>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          <div className={`toast-icon ${toast.type}`}>
            {toast.type === "success" ? <CheckIcon /> : <AlertIcon />}
          </div>
          <div className="toast-text">
            <h4>{toast.title}</h4>
            <p>{toast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardLayout;